import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, Users, Building2, Cpu, HardDrive, 
  Sparkles, DollarSign, Calculator, Target, Zap, 
  Award, Flame, PieChart
} from 'lucide-react';

interface RevenueSimulatorTabProps {
  currentPayingTenants?: number;
  currentInicioCount?: number;
  currentEstudioCount?: number;
  currentConstructoraCount?: number;
  currentEnterpriseCount?: number;
}

export default function RevenueSimulatorTab({
  currentPayingTenants = 0,
  currentInicioCount = 0,
  currentEstudioCount = 0,
  currentConstructoraCount = 0,
  currentEnterpriseCount = 0
}: RevenueSimulatorTabProps) {
  // Cantidad de clientes simulados por plan
  const [inicioCount, setInicioCount] = useState<number>(currentInicioCount || 15);
  const [estudioCount, setEstudioCount] = useState<number>(currentEstudioCount || 20);
  const [constructoraCount, setConstructoraCount] = useState<number>(currentConstructoraCount || 5);
  const [enterpriseCount, setEnterpriseCount] = useState<number>(currentEnterpriseCount || 1);

  // Selector de esquema de precios: Lanzamiento vs Lista Regular
  const [pricingTier, setPricingTier] = useState<'launch' | 'regular'>('launch');

  // Porcentaje de clientes que pagan con descuento anual (20% OFF)
  const [annualMixPercent, setAnnualMixPercent] = useState<number>(25);

  // Precios mensuales dinámicos según el esquema seleccionado (MXN)
  const PRICES = useMemo(() => {
    if (pricingTier === 'launch') {
      return {
        inicio: 490,
        estudio: 890,
        constructora: 1690,
        enterprise: 4990
      };
    }
    return {
      inicio: 990,
      estudio: 1890,
      constructora: 3490,
      enterprise: 8990
    };
  }, [pricingTier]);

  // Capacidades por plan
  const CAPACITY = {
    inicio: { users: 3, projects: 2, aiScans: 500 },
    estudio: { users: 10, projects: 5, aiScans: 1500 },
    constructora: { users: 30, projects: 15, aiScans: 5000 },
    enterprise: { users: 75, projects: 35, aiScans: 15000 }
  };

  // Cálculos reactivos
  const simulation = useMemo(() => {
    const totalClients = inicioCount + estudioCount + constructoraCount + enterpriseCount;

    // Ingresos base mensuales a precio de lista
    const baseMRR = 
      (inicioCount * PRICES.inicio) +
      (estudioCount * PRICES.estudio) +
      (constructoraCount * PRICES.constructora) +
      (enterpriseCount * PRICES.enterprise);

    // Ajuste por mix anual (los anuales pagan con 20% de descuento)
    const annualDiscountFactor = 1 - (annualMixPercent / 100) * 0.20;
    const effectiveMRR = Math.round(baseMRR * annualDiscountFactor);
    const effectiveARR = effectiveMRR * 12;

    const mrrUSD = Math.round(effectiveMRR / 20);
    const arrUSD = Math.round(effectiveARR / 20);

    // Costos Operativos de Infraestructura Estimados (Google Cloud Firestore + Gemini 2.5 Flash + Storage)
    // Costo promedio por cliente activo de ~$18 MXN a $32 MXN mensuales
    const estimatedInfraCostMXN = Math.round(
      (inicioCount * 12) +
      (estudioCount * 24) +
      (constructoraCount * 55) +
      (enterpriseCount * 120)
    );

    const netMonthlyProfitMXN = Math.max(0, effectiveMRR - estimatedInfraCostMXN);
    const netAnnualProfitMXN = netMonthlyProfitMXN * 12;
    const profitMarginPercent = effectiveMRR > 0 ? Math.round((netMonthlyProfitMXN / effectiveMRR) * 100) : 100;

    // Volumen operativo total
    const totalProjects = 
      (inicioCount * CAPACITY.inicio.projects) +
      (estudioCount * CAPACITY.estudio.projects) +
      (constructoraCount * CAPACITY.constructora.projects) +
      (enterpriseCount * CAPACITY.enterprise.projects);

    const totalUsers = 
      (inicioCount * CAPACITY.inicio.users) +
      (estudioCount * CAPACITY.estudio.users) +
      (constructoraCount * CAPACITY.constructora.users) +
      (enterpriseCount * CAPACITY.enterprise.users);

    const totalAiScans = 
      (inicioCount * CAPACITY.inicio.aiScans) +
      (estudioCount * CAPACITY.estudio.aiScans) +
      (constructoraCount * CAPACITY.constructora.aiScans) +
      (enterpriseCount * CAPACITY.enterprise.aiScans);

    // Desglose de ingresos por plan
    const revInicio = inicioCount * PRICES.inicio * annualDiscountFactor;
    const revEstudio = estudioCount * PRICES.estudio * annualDiscountFactor;
    const revConstructora = constructoraCount * PRICES.constructora * annualDiscountFactor;
    const revEnterprise = enterpriseCount * PRICES.enterprise * annualDiscountFactor;

    return {
      totalClients,
      effectiveMRR,
      effectiveARR,
      mrrUSD,
      arrUSD,
      estimatedInfraCostMXN,
      netMonthlyProfitMXN,
      netAnnualProfitMXN,
      profitMarginPercent,
      totalProjects,
      totalUsers,
      totalAiScans,
      breakdown: {
        inicio: { amount: revInicio, percent: effectiveMRR > 0 ? Math.round((revInicio / effectiveMRR) * 100) : 0 },
        estudio: { amount: revEstudio, percent: effectiveMRR > 0 ? Math.round((revEstudio / effectiveMRR) * 100) : 0 },
        constructora: { amount: revConstructora, percent: effectiveMRR > 0 ? Math.round((revConstructora / effectiveMRR) * 100) : 0 },
        enterprise: { amount: revEnterprise, percent: effectiveMRR > 0 ? Math.round((revEnterprise / effectiveMRR) * 100) : 0 }
      }
    };
  }, [inicioCount, estudioCount, constructoraCount, enterpriseCount, annualMixPercent, PRICES, pricingTier]);

  // Presets de escenarios
  const applyPreset = (preset: 'validation' | 'takeoff' | 'growth' | 'national' | 'leader') => {
    switch (preset) {
      case 'validation':
        setInicioCount(6);
        setEstudioCount(3);
        setConstructoraCount(1);
        setEnterpriseCount(0);
        break;
      case 'takeoff':
        setInicioCount(25);
        setEstudioCount(20);
        setConstructoraCount(5);
        setEnterpriseCount(0);
        break;
      case 'growth':
        setInicioCount(70);
        setEstudioCount(60);
        setConstructoraCount(20);
        setEnterpriseCount(2);
        break;
      case 'national':
        setInicioCount(180);
        setEstudioCount(140);
        setConstructoraCount(50);
        setEnterpriseCount(10);
        break;
      case 'leader':
        setInicioCount(500);
        setEstudioCount(400);
        setConstructoraCount(150);
        setEnterpriseCount(25);
        break;
    }
  };

  const loadCurrentRealData = () => {
    setInicioCount(currentInicioCount || 2);
    setEstudioCount(currentEstudioCount || 1);
    setConstructoraCount(currentConstructoraCount || 0);
    setEnterpriseCount(currentEnterpriseCount || 0);
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header Banner */}
      <div className="p-6 bg-gradient-to-r from-[#0a2540] via-slate-900 to-indigo-950 rounded-3xl border border-indigo-500/20 shadow-xl text-white relative overflow-hidden">
        <div className="absolute -right-10 -bottom-10 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-[10px] font-black uppercase tracking-widest mb-2">
              <Sparkles size={12} className="animate-pulse" /> Simulador Financiero ArchiCore
            </div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight">
              Proyección de Clientes, <span className="text-orange-400">MRR</span> y <span className="text-cyan-400">ARR</span>
            </h2>
            <p className="text-xs text-slate-300 max-w-2xl mt-1">
              Ajusta el volumen de despachos y constructoras para estimar tus ingresos mensuales, anuales y utilidad neta en tiempo real.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Switch: Precio Lanzamiento vs Lista */}
            <div className="flex items-center p-1 bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 shadow-inner">
              <button
                type="button"
                onClick={() => setPricingTier('launch')}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  pricingTier === 'launch'
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <span>🏷️ Precio Lanzamiento</span>
                <span className="text-[10px] opacity-85 font-mono hidden md:inline">($490 / $890 / $1,690)</span>
              </button>

              <button
                type="button"
                onClick={() => setPricingTier('regular')}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  pricingTier === 'regular'
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <span>💎 Precio de Lista Regular</span>
                <span className="text-[10px] opacity-85 font-mono hidden md:inline">($990 / $1,890 / $3,490)</span>
              </button>
            </div>

            <button
              onClick={loadCurrentRealData}
              className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-slate-200 border border-white/15 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap"
            >
              <Users size={14} />
              Cargar Reales
            </button>
          </div>
        </div>

        {/* Quick Scenario Preset Pills */}
        <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider shrink-0 mr-1 flex items-center gap-1">
            <Target size={14} className="text-orange-400" /> Metas / Escenarios:
          </span>

          <button
            onClick={() => applyPreset('validation')}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/15 text-slate-200 border border-white/10 transition-all whitespace-nowrap"
          >
            🎯 10 Clientes (Validación)
          </button>

          <button
            onClick={() => applyPreset('takeoff')}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/15 text-slate-200 border border-white/10 transition-all whitespace-nowrap"
          >
            🚀 50 Clientes (Despegue)
          </button>

          <button
            onClick={() => applyPreset('growth')}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 transition-all whitespace-nowrap"
          >
            🏆 150 Clientes ($1.4M ARR)
          </button>

          <button
            onClick={() => applyPreset('national')}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-400/30 transition-all whitespace-nowrap"
          >
            👑 350 Clientes ($3.4M ARR)
          </button>

          <button
            onClick={() => applyPreset('leader')}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-cyan-500/30 to-purple-500/30 hover:opacity-90 text-cyan-200 border border-cyan-400/40 transition-all whitespace-nowrap"
          >
            🦄 1,000 Clientes ($10M ARR)
          </button>
        </div>
      </div>

      {/* Main KPI Results Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Card 1: MRR Proyectado */}
        <div className="p-5 rounded-3xl bg-white border border-orange-200 shadow-sm relative overflow-hidden">
          <div className="flex justify-between items-start mb-3">
            <div className="p-3 bg-orange-500 text-white rounded-2xl shadow-md">
              <DollarSign size={22} />
            </div>
            <span className="text-[10px] bg-orange-100 text-orange-800 px-2.5 py-1 rounded-full font-black uppercase tracking-wider">
              MRR Mensual
            </span>
          </div>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Ingreso Mensual Recurrente</p>
          <h3 className="text-3xl font-black text-slate-900 mt-1">
            ${simulation.effectiveMRR.toLocaleString()} <span className="text-sm font-bold text-slate-500">MXN</span>
          </h3>
          <p className="text-xs text-slate-500 mt-1.5 font-medium">
            ≈ <strong>${simulation.mrrUSD.toLocaleString()} USD</strong> / mes ({simulation.totalClients} empresas)
          </p>
        </div>

        {/* Card 2: ARR Proyectado */}
        <div className="p-5 rounded-3xl bg-gradient-to-br from-indigo-900 to-indigo-950 text-white shadow-lg relative overflow-hidden">
          <div className="flex justify-between items-start mb-3">
            <div className="p-3 bg-indigo-500/30 text-indigo-300 border border-indigo-400/30 rounded-2xl">
              <TrendingUp size={22} />
            </div>
            <span className="text-[10px] bg-indigo-400/20 text-indigo-200 border border-indigo-400/30 px-2.5 py-1 rounded-full font-black uppercase tracking-wider">
              👑 ARR Anual
            </span>
          </div>
          <p className="text-[11px] font-bold text-indigo-200/80 uppercase tracking-wider">Ingreso Anual Proyectado</p>
          <h3 className="text-3xl font-black text-white mt-1">
            ${simulation.effectiveARR.toLocaleString()} <span className="text-sm font-bold text-indigo-300">MXN</span>
          </h3>
          <p className="text-xs text-indigo-200 mt-1.5 font-medium">
            ≈ <strong>${simulation.arrUSD.toLocaleString()} USD</strong> / año facturado
          </p>
        </div>

        {/* Card 3: Utilidad Neta & Margen */}
        <div className="p-5 rounded-3xl bg-white border border-emerald-200 shadow-sm relative overflow-hidden">
          <div className="flex justify-between items-start mb-3">
            <div className="p-3 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-2xl">
              <Award size={22} />
            </div>
            <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full font-black uppercase tracking-wider">
              {simulation.profitMarginPercent}% Margen
            </span>
          </div>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Utilidad Neta Anual Estimada</p>
          <h3 className="text-3xl font-black text-emerald-600 mt-1">
            ${simulation.netAnnualProfitMXN.toLocaleString()} <span className="text-sm font-bold text-slate-500">MXN</span>
          </h3>
          <p className="text-xs text-slate-500 mt-1.5 font-medium">
            Infraestructura estimada: ${simulation.estimatedInfraCostMXN.toLocaleString()} MXN/mes
          </p>
        </div>

        {/* Card 4: Impacto en la Industria */}
        <div className="p-5 rounded-3xl bg-white border border-slate-200 shadow-sm">
          <div className="flex justify-between items-start mb-3">
            <div className="p-3 bg-cyan-50 text-cyan-600 border border-cyan-100 rounded-2xl">
              <Zap size={22} />
            </div>
            <span className="text-[10px] bg-cyan-100 text-cyan-800 px-2.5 py-1 rounded-full font-black uppercase tracking-wider">
              Ecosistema
            </span>
          </div>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Obras y Profesionales</p>
          <h3 className="text-3xl font-black text-slate-900 mt-1">
            {simulation.totalProjects.toLocaleString()} <span className="text-sm font-normal text-slate-400">obras</span>
          </h3>
          <p className="text-xs text-slate-500 mt-1.5 font-medium">
            {simulation.totalUsers.toLocaleString()} usuarios • {(simulation.totalAiScans / 1000).toFixed(0)}k escaneos/mes
          </p>
        </div>
      </div>

      {/* Simulator Sliders & Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Sliders (8 cols) */}
        <div className="lg:col-span-8 bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2.5">
              <Calculator className="text-indigo-600" size={20} />
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-800">
                Ajuste de Clientes por Plan
              </h3>
            </div>
            <span className="text-xs font-bold text-slate-500">
              Total: <strong className="text-slate-900 text-sm">{simulation.totalClients}</strong> empresas
            </span>
          </div>

          {/* Slider 1: Plan Inicio */}
          <div className="space-y-2 p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-cyan-800 bg-cyan-100 px-2 py-0.5 rounded-md">
                  Plan Inicio
                </span>
                <span className="text-xs text-slate-500 ml-2 font-semibold">(${PRICES.inicio.toLocaleString()} MXN • 3 us / 2 obras)</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="2000"
                  value={inicioCount}
                  onChange={(e) => setInicioCount(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-20 px-2.5 py-1 text-right font-black text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <span className="text-xs font-bold text-slate-500">empresas</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="500"
              value={inicioCount}
              onChange={(e) => setInicioCount(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
            />
            <div className="flex justify-between text-[10px] text-slate-400 font-medium">
              <span>0</span>
              <span>Subtotal: ${(inicioCount * PRICES.inicio).toLocaleString()} MXN/mes</span>
              <span>500+</span>
            </div>
          </div>

          {/* Slider 2: Plan Estudio */}
          <div className="space-y-2 p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded-md">
                  Plan Estudio
                </span>
                <span className="text-xs text-slate-500 ml-2 font-semibold">(${PRICES.estudio.toLocaleString()} MXN • 10 us / 5 obras)</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="2000"
                  value={estudioCount}
                  onChange={(e) => setEstudioCount(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-20 px-2.5 py-1 text-right font-black text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs font-bold text-slate-500">empresas</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="400"
              value={estudioCount}
              onChange={(e) => setEstudioCount(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-[10px] text-slate-400 font-medium">
              <span>0</span>
              <span>Subtotal: ${(estudioCount * PRICES.estudio).toLocaleString()} MXN/mes</span>
              <span>400+</span>
            </div>
          </div>

          {/* Slider 3: Plan Constructora */}
          <div className="space-y-2 p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-orange-800 bg-orange-100 px-2 py-0.5 rounded-md">
                  Plan Constructora
                </span>
                <span className="text-xs text-slate-500 ml-2 font-semibold">(${PRICES.constructora.toLocaleString()} MXN • 30 us / 15 obras)</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={constructoraCount}
                  onChange={(e) => setConstructoraCount(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-20 px-2.5 py-1 text-right font-black text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <span className="text-xs font-bold text-slate-500">empresas</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="200"
              value={constructoraCount}
              onChange={(e) => setConstructoraCount(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
            />
            <div className="flex justify-between text-[10px] text-slate-400 font-medium">
              <span>0</span>
              <span>Subtotal: ${(constructoraCount * PRICES.constructora).toLocaleString()} MXN/mes</span>
              <span>200+</span>
            </div>
          </div>

          {/* Slider 4: Plan a Medida / Enterprise */}
          <div className="space-y-2 p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-purple-800 bg-purple-100 px-2 py-0.5 rounded-md">
                  Plan a Medida / Corporativo
                </span>
                <span className="text-xs text-slate-500 ml-2 font-semibold">(~${PRICES.enterprise.toLocaleString()} MXN promedio)</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="500"
                  value={enterpriseCount}
                  onChange={(e) => setEnterpriseCount(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-20 px-2.5 py-1 text-right font-black text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-xs font-bold text-slate-500">empresas</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="50"
              value={enterpriseCount}
              onChange={(e) => setEnterpriseCount(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <div className="flex justify-between text-[10px] text-slate-400 font-medium">
              <span>0</span>
              <span>Subtotal: ${(enterpriseCount * PRICES.enterprise).toLocaleString()} MXN/mes</span>
              <span>50+</span>
            </div>
          </div>

          {/* Slider 5: Porcentaje de Clientes con Pago Anual (-20% Desc) */}
          <div className="p-4 bg-amber-50/60 rounded-2xl border border-amber-200/70 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                <span>🏷️ Mix de Contratación Anual (-20% Descuento)</span>
              </span>
              <span className="text-xs font-black text-amber-900 bg-amber-200/60 px-2 py-0.5 rounded-md">
                {annualMixPercent}% de clientes pagan anual
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={annualMixPercent}
              onChange={(e) => setAnnualMixPercent(parseInt(e.target.value))}
              className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
            />
            <p className="text-[10px] text-amber-700 font-medium">
              Los clientes anuales pagan por adelantado 12 meses con 20% de descuento. Aumenta el flujo de caja inicial.
            </p>
          </div>
        </div>

        {/* Right Column: Breakdown & Financial Summary (4 cols) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Revenue Breakdown Box */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <PieChart className="text-indigo-600" size={18} />
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-700">
                Distribución de Ingresos
              </h3>
            </div>

            {/* Visual Stacked Progress Bar */}
            <div className="h-4 w-full bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
              <div style={{ width: `${simulation.breakdown.inicio.percent}%` }} className="bg-cyan-500 h-full transition-all duration-300" title={`Inicio: ${simulation.breakdown.inicio.percent}%`} />
              <div style={{ width: `${simulation.breakdown.estudio.percent}%` }} className="bg-indigo-600 h-full transition-all duration-300" title={`Estudio: ${simulation.breakdown.estudio.percent}%`} />
              <div style={{ width: `${simulation.breakdown.constructora.percent}%` }} className="bg-orange-500 h-full transition-all duration-300" title={`Constructora: ${simulation.breakdown.constructora.percent}%`} />
              <div style={{ width: `${simulation.breakdown.enterprise.percent}%` }} className="bg-purple-600 h-full transition-all duration-300" title={`A Medida: ${simulation.breakdown.enterprise.percent}%`} />
            </div>

            {/* Breakdown Items */}
            <div className="space-y-2.5 pt-2 text-xs">
              <div className="flex justify-between items-center p-2 rounded-xl bg-cyan-50/50 border border-cyan-100">
                <span className="flex items-center gap-1.5 font-bold text-cyan-900">
                  <span className="w-2.5 h-2.5 rounded-full bg-cyan-500" /> Plan Inicio ({inicioCount})
                </span>
                <span className="font-mono font-bold text-slate-800">${Math.round(simulation.breakdown.inicio.amount).toLocaleString()} MXN</span>
              </div>

              <div className="flex justify-between items-center p-2 rounded-xl bg-indigo-50/50 border border-indigo-100">
                <span className="flex items-center gap-1.5 font-bold text-indigo-900">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-600" /> Plan Estudio ({estudioCount})
                </span>
                <span className="font-mono font-bold text-slate-800">${Math.round(simulation.breakdown.estudio.amount).toLocaleString()} MXN</span>
              </div>

              <div className="flex justify-between items-center p-2 rounded-xl bg-orange-50/50 border border-orange-100">
                <span className="flex items-center gap-1.5 font-bold text-orange-900">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> Constructora ({constructoraCount})
                </span>
                <span className="font-mono font-bold text-slate-800">${Math.round(simulation.breakdown.constructora.amount).toLocaleString()} MXN</span>
              </div>

              <div className="flex justify-between items-center p-2 rounded-xl bg-purple-50/50 border border-purple-100">
                <span className="flex items-center gap-1.5 font-bold text-purple-900">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-600" /> A Medida ({enterpriseCount})
                </span>
                <span className="font-mono font-bold text-slate-800">${Math.round(simulation.breakdown.enterprise.amount).toLocaleString()} MXN</span>
              </div>
            </div>
          </div>

          {/* Unit Economics Box */}
          <div className="bg-slate-900 text-white p-6 rounded-3xl border border-slate-800 shadow-xl space-y-4">
            <div className="flex items-center gap-2 text-orange-400">
              <Flame size={18} />
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-200">
                Economía Unitaria SaaS
              </h3>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between py-1.5 border-b border-slate-800">
                <span className="text-slate-400">ARPU (Ingreso Promedio x Cliente):</span>
                <span className="font-bold text-white font-mono">
                  ${simulation.totalClients > 0 ? Math.round(simulation.effectiveMRR / simulation.totalClients).toLocaleString() : 0} MXN/mes
                </span>
              </div>

              <div className="flex justify-between py-1.5 border-b border-slate-800">
                <span className="text-slate-400">Costo Servidores Promedio x Cliente:</span>
                <span className="font-bold text-emerald-400 font-mono">
                  ~${simulation.totalClients > 0 ? (simulation.estimatedInfraCostMXN / simulation.totalClients).toFixed(1) : '0.0'} MXN/mes
                </span>
              </div>

              <div className="flex justify-between py-1.5 border-b border-slate-800">
                <span className="text-slate-400">Margen Bruto de Software:</span>
                <span className="font-bold text-cyan-400 font-mono">{simulation.profitMarginPercent}%</span>
              </div>

              <div className="flex justify-between py-1.5">
                <span className="text-slate-400">LTV Estimado (18 meses retención):</span>
                <span className="font-bold text-amber-400 font-mono">
                  ${simulation.totalClients > 0 ? Math.round((simulation.effectiveMRR / simulation.totalClients) * 18).toLocaleString() : 0} MXN
                </span>
              </div>
            </div>

            <div className="p-3 bg-white/5 rounded-2xl border border-white/10 text-[11px] text-slate-300 leading-relaxed">
              💡 <strong>Regla de Oro SaaS:</strong> Con un margen superior al 90%, ArchiCore tiene un modelo hiper-escalable. La infraestructura en Firebase y Gemini cuesta centavos comparado con el valor aportado en obra.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
